import React, { useState } from 'react';
import classNames from 'classnames';
import { IUICfgMixedContainer } from '@canvas-logic/clp-api-types';
import { Icon } from '@canvas-logic/clp-shared-ui';
import { ConfiguratorUIElementStates, viewElementId } from '@/elements/base/ElementState';
import { ContainerLevelContext } from '@/ui-kit/contexts/ContainerLevelContext';
import { gapLevelToCssValue } from '@/elements/shared/gapUtils';
import { ElementRenderer } from '@/elements/ElementRenderer';
import { useUiCfgGroupLabel } from '@/localization/useUiCfgGroupLabel';

interface MixedContainerCfgUIElementProps {
  viewElement: IUICfgMixedContainer;
  childrenItems: ConfiguratorUIElementStates[];
}

export const MixedContainerCfgUIElement: React.FC<MixedContainerCfgUIElementProps> = ({
  viewElement,
  childrenItems,
}) => {
  const [expanded, setExpanded] = useState(!viewElement.expandable || !!viewElement.defaultExpanded);

  // Get level from viewElement (set by virtualLayoutBuilder)
  const level = (viewElement as any)._level ?? 1;
  // Get parentLevel for title styling (title appears in parent's context)
  const parentLevel = (viewElement as any)._parentLevel ?? level;
  const titleLevel =
    viewElement.titleSize && viewElement.titleSize !== 'auto'
      ? parseInt(viewElement.titleSize.replace('level-', ''), 10)
      : parentLevel;

  // ADR-0045: resolve project-level translations for the container's group title /
  // description. The hook returns the live system value when no override matches.
  const translatedTitle = useUiCfgGroupLabel(viewElement.id, 'title', viewElement.title);
  const translatedDescription = useUiCfgGroupLabel(viewElement.id, 'desc', viewElement.description);

  const isGridMode = viewElement.combineIntoGrid;
  const hasHeader =
    (viewElement.showTitle && translatedTitle) || (viewElement.showDescription && translatedDescription);

  const groupGapCss = gapLevelToCssValue(viewElement.groupGapLevel);
  const afterNameGapCss = gapLevelToCssValue(viewElement.afterNameGap);

  return (
    <ContainerLevelContext.Provider value={level}>
      <div
        className={classNames('cp-mixed-container', {
          'cp-mixed-container--grid-mode': isGridMode,
          'cp-mixed-container--collapsed': viewElement.expandable && !expanded,
          'cp-mixed-container--expanded': viewElement.expandable && expanded,
        })}
        data-element-id={viewElement.id}
        data-container-type="mixed"
        data-container-level={level}
      >
        {/* Header - Title/Description/Expand Button */}
        {hasHeader && (
          <div
            className="cp-mixed-container__header"
            data-title-level={titleLevel}
            style={afterNameGapCss ? ({ '--after-name-gap': afterNameGapCss } as React.CSSProperties) : undefined}
          >
            {viewElement.showTitle && translatedTitle && (
              <h3 className="cp-mixed-container__title">{translatedTitle}</h3>
            )}
            {viewElement.expandable && (
              <button
                className="cp-mixed-container__expand-button"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                aria-expanded={expanded}
              >
                <Icon type={expanded ? 'chevronUp' : 'chevronDown'} />
              </button>
            )}
            {viewElement.showDescription && translatedDescription && (
              <p className="cp-mixed-container__description">{translatedDescription}</p>
            )}
          </div>
        )}

        {/* Content - Grid or Normal Layout */}
        <div className={classNames('cp-expandable-wrapper', !expanded && 'cp-expandable-wrapper--collapsed')}>
          <div className="cp-expandable-wrapper__inner">
            <div
              className={classNames('cp-mixed-container__content', {
                'cp-mixed-container__content--grid': isGridMode,
                'cp-mixed-container__content--normal': !isGridMode,
              })}
              style={groupGapCss ? ({ '--container-gap': groupGapCss } as React.CSSProperties) : undefined}
            >
              {childrenItems.map((child) => (
                <div
                  key={child.id || viewElementId(child)}
                  className={isGridMode ? 'cp-mixed-container__item--grid' : 'cp-mixed-container__item'}
                >
                  <ElementRenderer state={child} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ContainerLevelContext.Provider>
  );
};
